import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ObjectTypes, ApiErrorCodes, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import {FirmwareGetListSchema} from '../../../api/DBSchema/Firmware';
import store from '../../../store';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {dateToString} from '../../../utils/utils';
import {Link} from 'react-router-dom';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {buildBooleanSort, buildWeighedSort, buildNumberSort, buildStringSortCaseInsensitive} from '../../../utils/sortUtils';
import SMCHighlighter from '../../../components/Simple/SMCHighlighter';

const NameCell = React.memo(({value, object, textToHighlight}) => {
    if (value) {
        return (
            <Link to={`?id=${object[FirmwareGetListSchema.uid]}`} > <SMCHighlighter highlight={textToHighlight} text={value} /></Link>
        )
    }
    return null;
});
const ReleaseDateCell = React.memo(({value, textToHighlight}) => {
    return <SMCHighlighter highlight={textToHighlight} text={dateToString(value)} />;
});
const EnabledCell = React.memo(({value}) => {
    return value ? <SMCIcon icon="greenCheck" /> : <SMCIcon icon="disabled" />;
});

const applianceWeights = {
    [FirmwareGetListSchema.applianceValues.type600]:  0,
    [FirmwareGetListSchema.applianceValues.type1200]: 1,
    [FirmwareGetListSchema.applianceValues.typeS1S2]: 2,
    [FirmwareGetListSchema.applianceValues.typeV0V1]: 3,
    [FirmwareGetListSchema.applianceValues.typeV2V3]: 4,
}

export function useFirmwareResults() {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    // const { sortBasis, sortDirection, setSort } = useSort('name', SortDirections.ASCENDING);
    const { sortBasis, sortDirection, setSort } = useSort('release_date', SortDirections.DESCENDING);

    const columns = useMemo(() => ([
        { id: 'name',         accessor: FirmwareGetListSchema.name,        header: t('SMP.NAME'),                  width: 315,                  defaultShow: true, sort: buildStringSortCaseInsensitive(FirmwareGetListSchema.name),         Cell: NameCell        },
        { id: 'appliance',    accessor: FirmwareGetListSchema.appliance,   header: t('SMP.FIRMWARE.APPLIANCE'),    width: 150,                  defaultShow: true, sort: buildWeighedSort(FirmwareGetListSchema.appliance, applianceWeights)                       },
        { id: 'release_date', accessor: FirmwareGetListSchema.releaseDate, header: t('SMP.FIRMWARE.RELEASE_DATE'), width: 160,                  defaultShow: true, sort: buildNumberSort(FirmwareGetListSchema.releaseDate),                 Cell: ReleaseDateCell },
        { id: 'version',      accessor: FirmwareGetListSchema.version,     header: t('SMP.FIRMWARE.VERSION'),      width: 155,                  defaultShow: true, sort: buildStringSortCaseInsensitive(FirmwareGetListSchema.version)                             },
        { id: 'description',  accessor: FirmwareGetListSchema.description, header: t('SMP.DESCRIPTION'),           width: 330,                  defaultShow: true, sort: buildStringSortCaseInsensitive(FirmwareGetListSchema.description)                         },
        { id: 'type',         accessor: FirmwareGetListSchema.type,        header: t('SMP.FIRMWARE.TYPE'),         width: 200,                  defaultShow: true, sort: buildStringSortCaseInsensitive(FirmwareGetListSchema.type)                                },
        { id: 'enabled',      accessor: FirmwareGetListSchema.enabled,     header: t('SMP.ENABLED'),               width: 70,  align: 'center', defaultShow: true, sort: buildBooleanSort(FirmwareGetListSchema.enabled),                    Cell: EnabledCell     }
    ]), [t]);
    
    const rowIdSelector = obj => obj[FirmwareGetListSchema.uid];
    
    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.FIRMWARE, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.FIRMWARE, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const isFirstRender = useRef(true);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        filterObject,
        freeTextSearch,
        setFilterObject,
        isLastPage,
        reloadTable,
        reloadTableNoClearSelected,
        selectedObjects,
        setSelectedObjects,
        deleteSelectedObjects
    } = useGenericResultsDataApi(ObjectTypes.FIRMWARE);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES'))) {
            setIsPageLoaded(false);
            deleteSelectedObjects()
                    .then(({successMessage, errorMessage}) => {
                        successMessage && enqueueSuccessSnackbar(successMessage);
                        errorMessage && enqueueErrorSnackbar(errorMessage);
                        reloadTable();
                    })
                    .catch(e => {
                        const errorMessage = getErrorMessage(e);
                        enqueueErrorSnackbar(errorMessage);
                    })
                    .finally(() => {
                        setIsPageLoaded(true);
                    });
        }
    }, [selectedObjects, deleteSelectedObjects, reloadTable, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    // Load table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);

    // Reload table without clearing selected on filter changed
    useEffect(() => {
        // Don't run on first render, only when filter is changed
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        reloadTableNoClearSelected()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTableNoClearSelected, filterObject, enqueueErrorSnackbar, t]);
    
    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        reloadButtonOnClick,
        deleteButtonOnClick,
    });
}